import React, { useState, useRef, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';

// Popular skills categorized
const SKILL_SUGGESTIONS = [
  // Programming Languages
  'JavaScript', 'Python', 'Java', 'C++', 'C#', 'PHP', 'Ruby', 'Go', 'Swift', 'Kotlin',
  'TypeScript', 'Rust', 'R', 'MATLAB', 'Scala', 'Perl', 'Shell Scripting',
  // Frontend
  'React', 'Angular', 'Vue.js', 'HTML', 'CSS', 'Tailwind CSS', 'Bootstrap', 'SASS',
  'Next.js', 'Redux', 'Material-UI', 'Responsive Design', 'UI/UX Design',
  // Backend
  'Node.js', 'Express.js', 'Django', 'Flask', 'Spring Boot', 'Laravel', 'Ruby on Rails',
  'ASP.NET', 'GraphQL', 'REST API', 'Microservices',
  // Databases
  'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'SQL Server', 'Firebase',
  'DynamoDB', 'Cassandra', 'Elasticsearch',
  // DevOps & Cloud
  'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins', 'CI/CD',
  'Git', 'GitHub', 'GitLab', 'Linux', 'Nginx', 'Apache',
  // Mobile Development
  'React Native', 'Flutter', 'iOS Development', 'Android Development', 'Xamarin',
  // Data Science & AI
  'Machine Learning', 'Deep Learning', 'TensorFlow', 'PyTorch', 'Data Analysis',
  'Data Visualization', 'Pandas', 'NumPy', 'NLP', 'Computer Vision',
  // Soft Skills
  'Communication', 'Leadership', 'Team Management', 'Problem Solving', 'Critical Thinking',
  'Time Management', 'Project Management', 'Agile', 'Scrum', 'Public Speaking',
  // Other Tech Skills
  'Testing', 'Jest', 'Selenium', 'Cypress', 'QA', 'Security', 'Blockchain',
  'Data Structures', 'Algorithms', 'System Design', 'OOP', 'Design Patterns'
];

/**
 * Skills Multi-Selector with Autocomplete
 * @param {Array} selectedSkills - Array of currently selected skills
 * @param {Function} onChange - Callback when skills change
 * @param {Number} maxSkills - Maximum number of skills allowed (default: 10)
 * @param {Boolean} required - Whether skills are required
 * @param {String} placeholder - Placeholder text
 */
const SkillsSelector = ({ 
  selectedSkills = [], 
  onChange, 
  maxSkills = 10,
  required = false,
  placeholder = 'Search and add required skills...'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = SKILL_SUGGESTIONS.filter(skill =>
        skill.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedSkills.some(s => s.toLowerCase() === skill.toLowerCase())
      );

      setFilteredSuggestions(filtered.slice(0, 12)); // Limit to 12 suggestions
    } else {
      // Show popular skills when input is empty
      const popular = SKILL_SUGGESTIONS.filter(skill =>
        !selectedSkills.some(s => s.toLowerCase() === skill.toLowerCase())
      );

      setFilteredSuggestions(popular.slice(0, 12));
    }
  }, [inputValue, selectedSkills]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addSkill = (skill) => {
    const trimmedSkill = (skill || inputValue).trim();
    
    if (!trimmedSkill) return;
    
    if (selectedSkills.length >= maxSkills) {
      alert(`You can only add up to ${maxSkills} skills`);
      return;
    }
    
    if (selectedSkills.some(s => s.toLowerCase() === trimmedSkill.toLowerCase())) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }
    
    onChange([...selectedSkills, trimmedSkill]);
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeSkill = (skillToRemove) => {
    onChange(selectedSkills.filter(skill => skill !== skillToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    } else if (e.key === 'Backspace' && !inputValue && selectedSkills.length > 0) {
      // Remove last skill on backspace if input is empty
      removeSkill(selectedSkills[selectedSkills.length - 1]);
    }
  };

  return (
    <div className="w-full">
      {/* Selected Skills Tags */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedSkills.map((skill, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 rounded-full text-sm font-medium"
            >
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="hover:bg-gray-800 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${skill}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          required={required && selectedSkills.length === 0}
          autoComplete="off"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
        />

        {/* Autocomplete Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs text-gray-600 font-medium">
                {inputValue.trim() ? 'Matching Skills' : 'Popular Skills'}
              </p>
            </div>
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => addSkill(suggestion)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center justify-between group"
              >
                <span>{suggestion}</span>
                <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to add
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <p>Start typing to see suggestions or press Enter to add custom skill</p>
        <span className={selectedSkills.length >= maxSkills ? 'text-red-600 font-medium' : ''}>
          {selectedSkills.length} / {maxSkills} skills
        </span>
      </div>
    </div>
  );
};

export default SkillsSelector;
